import { useState } from "react";
import { useNotifications } from "@/hooks/useNotifications";
import { useInterviews } from "@/hooks/useInterviews";
import { useUserConnection } from "@/hooks/useUserConnection";
import { useCollaborationInviteActions } from "@/hooks/useCollaborationInviteActions";
import { useRelativeTime } from "@/hooks/useRelativeTime";
import { useIsMobile } from "@/hooks/use-mobile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { 
  Check, 
  X, 
  ExternalLink, 
  Video, 
  Calendar, 
  XCircle,
  MessageSquare,
  AlertCircle,
  FileText,
  UserCheck,
  Bell,
  Briefcase,
  Mail,
  UserPlus,
  AtSign,
  Users,
  type LucideIcon
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RescheduleInterviewDialog } from "@/components/ats/RescheduleInterviewDialog";
import { NotificationPreviewCard } from "@/components/notifications/NotificationPreviewCard";
import { NotificationQuickActions } from "@/components/notifications/NotificationQuickActions";

interface NotificationItemProps {
  notification: any;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
}

// Live-updating timestamp component
function NotificationTimestamp({ createdAt }: { createdAt: string }) {
  const relativeTime = useRelativeTime(createdAt);
  return (
    <span className="text-xs text-muted-foreground">
      {relativeTime}
    </span>
  );
}

export function NotificationItem({ 
  notification, 
  isSelected = false,
  onToggleSelect 
}: NotificationItemProps) {
  const { markAsRead, deleteNotification, archiveNotification } = useNotifications();
  const { cancelInterview } = useInterviews();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isUnread = !notification.read_at;
  
  // Connection notification handling
  const relatedUserId = notification.related_user_id;
  const isConnectionRequest = notification.type === "connection_request_received";
  const { 
    acceptRequest, 
    declineRequest, 
    isUpdating: isConnectionUpdating 
  } = useUserConnection(relatedUserId || "");
  
  // Collaboration invite handling
  const isCollaborationInvite = notification.type === "collaboration_invite";
  const collaborationMetadata = notification.metadata as any;
  const entityType = collaborationMetadata?.entity_type;
  const entityId = collaborationMetadata?.entity_id;
  const {
    acceptInvite,
    declineInvite,
    isUpdating: isCollaborationUpdating
  } = useCollaborationInviteActions();
  
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  
  // Extract interview metadata
  const isInterviewNotification = notification.type === "interview_scheduled" && 
                                   notification.related_interview_id;
  const interviewMetadata = notification.metadata as any;
  const meetingLink = interviewMetadata?.meeting_link;
  const scheduledAt = interviewMetadata?.interview_date && interviewMetadata?.interview_time
    ? new Date(`${interviewMetadata.interview_date}T${interviewMetadata.interview_time}`)
    : null;
  
  // Determine which quick actions to show
  const now = Date.now();
  const timeUntilInterview = scheduledAt ? scheduledAt.getTime() - now : 0;
  const showJoinButton = Boolean(meetingLink && scheduledAt && timeUntilInterview <= 60 * 60 * 1000 && timeUntilInterview > 0);
  const showRescheduleButton = Boolean(scheduledAt && timeUntilInterview > 0);
  const showCancelButton = Boolean(scheduledAt && timeUntilInterview > 0);
  
  // Get notification type icon
  const getNotificationIcon = (): { Icon: LucideIcon; colorClass: string; bgClass: string } => {
    switch (notification.type) {
      case "connection_request_received":
        return { Icon: UserPlus, colorClass: "text-blue-600", bgClass: "bg-blue-100" };
      case "connection_request_accepted":
        return { Icon: UserCheck, colorClass: "text-green-600", bgClass: "bg-green-100" };
      case "mention":
        return { Icon: AtSign, colorClass: "text-purple-600", bgClass: "bg-purple-100" };
      case "collaboration_invite":
        return { Icon: Users, colorClass: "text-cyan-600", bgClass: "bg-cyan-100" };
      case "interview_scheduled":
        return { Icon: Calendar, colorClass: "text-blue-600", bgClass: "bg-blue-100" };
      case "application_status":
        return { Icon: FileText, colorClass: "text-green-600", bgClass: "bg-green-100" };
      case "message":
        return { Icon: MessageSquare, colorClass: "text-purple-600", bgClass: "bg-purple-100" };
      case "application_received":
        return { Icon: Briefcase, colorClass: "text-indigo-600", bgClass: "bg-indigo-100" };
      case "candidate_applied":
        return { Icon: UserCheck, colorClass: "text-teal-600", bgClass: "bg-teal-100" };
      case "email":
        return { Icon: Mail, colorClass: "text-orange-600", bgClass: "bg-orange-100" };
      case "alert":
      case "critical":
        return { Icon: AlertCircle, colorClass: "text-red-600", bgClass: "bg-red-100" };
      default:
        return { Icon: Bell, colorClass: "text-gray-600", bgClass: "bg-gray-100" };
    }
  };
  
  const { Icon: NotificationIcon, colorClass, bgClass } = getNotificationIcon();
  
  // World-class: Get contextual action label for mentions based on entity type
  const getMentionActionLabel = () => {
    const metadata = notification.metadata as any;
    const entityType = metadata?.entity_type;
    switch (entityType) {
      case 'list': return 'View List';
      case 'task': return 'View Task';
      case 'note': return 'View Note';
      case 'event': return 'View Event';
      default: return notification.action_label || 'View';
    }
  };
  
  // World-class: Get contextual action icon for mentions
  const getMentionActionIcon = () => {
    const metadata = notification.metadata as any;
    const entityType = metadata?.entity_type;
    switch (entityType) {
      case 'list': return <FileText className="mr-1.5 h-3.5 w-3.5" />;
      case 'task': return <Check className="mr-1.5 h-3.5 w-3.5" />;
      default: return <ExternalLink className="mr-1.5 h-3.5 w-3.5" />;
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking checkbox or action buttons
    if ((e.target as HTMLElement).closest('[data-no-navigate]')) {
      return;
    }
    
    if (isUnread) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const getPriorityColor = () => {
    switch (notification.priority) {
      case "critical":
        return "bg-red-500";
      case "high":
        return "bg-orange-500";
      case "medium":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  const actorProfile = notification.actor?.profiles;

  return (
    <div
      className={cn(
        "p-3 sm:p-4 min-h-[56px] hover:bg-accent/50 transition-colors cursor-pointer relative group touch-target",
        isUnread && "bg-accent/30",
        isSelected && "bg-primary/10"
      )}
      onClick={handleClick}
    >
      {/* Priority indicator */}
      <div className={cn("absolute left-0 top-0 w-1 h-full", getPriorityColor())} />

      <div className="flex gap-3 pl-2">
        {/* Selection checkbox */}
        {onToggleSelect && (
          <div data-no-navigate className="flex items-center">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(notification.id)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Avatar with Type Icon Badge */}
        <div className="relative flex-shrink-0">
          <Avatar className="h-10 w-10">
            <AvatarImage src={actorProfile?.avatar_url} />
            <AvatarFallback>
              {actorProfile?.full_name?.[0] || notification.title[0]}
            </AvatarFallback>
          </Avatar>
          <div className={cn(
            "absolute -bottom-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center border-2 border-background",
            bgClass
          )}>
            <NotificationIcon className={cn("h-3 w-3", colorClass)} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className={cn("text-sm", isUnread && "font-semibold")}>
                {notification.title}
              </p>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {notification.message}
              </p>
            </div>
            
            {/* Quick Actions - Desktop uses dropdown, mobile uses inline buttons */}
            <div data-no-navigate className="flex items-center gap-1">
              {isMobile ? (
                <>
                  {isUnread && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 touch-target"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 touch-target"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <NotificationQuickActions
                  notificationId={notification.id}
                  isRead={!isUnread}
                  actionUrl={notification.action_url}
                  onMarkAsRead={() => markAsRead(notification.id)}
                  onArchive={() => archiveNotification(notification.id)}
                  onDelete={() => deleteNotification(notification.id)}
                />
              )}
            </div>
          </div>

          {/* Footer - Only show timestamp, remove redundant link when we have action button */}
          <div className="flex items-center gap-2 mt-1">
            <NotificationTimestamp createdAt={notification.created_at} />
          </div>
          
          {/* Quick Actions for Mention Notifications - World-class: Single contextual action */}
          {notification.type === "mention" && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-no-navigate>
              <Button
                size="sm"
                variant="default"
                onClick={(e) => {
                  e.stopPropagation();
                  markAsRead(notification.id);
                  // Navigate to the specific entity with comment highlighting
                  const actionUrl = notification.action_url || '/harmony';
                  navigate(actionUrl);
                }}
                className="min-h-[44px] px-4"
              >
                {getMentionActionIcon()}
                {getMentionActionLabel()}
              </Button>
            </div>
          )}
          
          {/* Quick Actions for Connection Request Notifications */}
          {isConnectionRequest && relatedUserId && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-no-navigate>
              <Button
                size="sm"
                variant="default"
                onClick={(e) => {
                  e.stopPropagation();
                  const connectionId = notification.metadata?.connection_id;
                  if (connectionId) {
                    acceptRequest(connectionId, {
                      onSuccess: () => {
                        markAsRead(notification.id);
                      }
                    });
                  }
                }}
                disabled={isConnectionUpdating}
                className="min-h-[44px] px-4"
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Accept
              </Button>
              
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  const connectionId = notification.metadata?.connection_id;
                  if (connectionId) {
                    declineRequest(connectionId, {
                      onSuccess: () => {
                        markAsRead(notification.id);
                      }
                    });
                  }
                }}
                disabled={isConnectionUpdating}
                className="min-h-[44px] px-4"
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                Decline
              </Button>
            </div>
          )}
          
          {/* Quick Actions for Collaboration Invite Notifications - World-class: Accept leads to content */}
          {isCollaborationInvite && entityType && entityId && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-no-navigate>
              <Button
                size="sm"
                variant="default"
                onClick={(e) => {
                  e.stopPropagation();
                  acceptInvite.mutate(
                    { entityType, entityId },
                    {
                      onSuccess: () => {
                        markAsRead(notification.id);
                        // Navigate directly to the shared entity
                        if (entityType === 'list') {
                          navigate(`/harmony?list=${entityId}`);
                        } else if (entityType === 'task') {
                          navigate(`/harmony?task=${entityId}`);
                        } else {
                          navigate('/harmony');
                        }
                      }
                    }
                  );
                }}
                disabled={isCollaborationUpdating}
                className="min-h-[44px] px-4"
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Accept & View {entityType === 'list' ? 'List' : entityType === 'task' ? 'Task' : ''}
              </Button>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  declineInvite.mutate(
                    { entityType, entityId },
                    {
                      onSuccess: () => {
                        markAsRead(notification.id);
                      }
                    }
                  );
                }}
                disabled={isCollaborationUpdating}
                className="min-h-[44px] px-4 text-muted-foreground"
              >
                Decline
              </Button>
            </div>
          )}
          
          {/* Quick Actions for Interview Notifications */}
          {isInterviewNotification && (showJoinButton || showRescheduleButton || showCancelButton) && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-no-navigate>
              {showJoinButton && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(meetingLink, '_blank');
                    markAsRead(notification.id);
                  }}
                  className="min-h-[44px] px-4"
                >
                  <Video className="mr-1.5 h-3.5 w-3.5" />
                  Join Meeting
                </Button>
              )}
              
              {showRescheduleButton && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRescheduleOpen(true);
                  }}
                  className="min-h-[44px] px-4"
                >
                  <Calendar className="mr-1.5 h-3.5 w-3.5" />
                  Reschedule
                </Button>
              )}
              
              {showCancelButton && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCancelDialogOpen(true);
                  }}
                  className="min-h-[44px] px-4"
                >
                  <XCircle className="mr-1.5 h-3.5 w-3.5" />
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Reschedule Dialog */}
      {isInterviewNotification && scheduledAt && (
        <RescheduleInterviewDialog
          open={rescheduleOpen}
          onOpenChange={setRescheduleOpen}
          interviewId={notification.related_interview_id!}
          currentDateTime={scheduledAt}
          onSuccess={() => {
            markAsRead(notification.id);
            setRescheduleOpen(false);
          }}
        />
      )}
      
      {/* Cancel Confirmation Dialog */}
      {isInterviewNotification && (
        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Interview?</AlertDialogTitle>
              <AlertDialogDescription>
                This will cancel the interview. The candidate will be notified about the cancellation.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Interview</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => {
                  cancelInterview.mutate(
                    { id: notification.related_interview_id!, reason: "Cancelled from notification" },
                    {
                      onSuccess: () => {
                        deleteNotification(notification.id);
                        setCancelDialogOpen(false);
                      }
                    }
                  );
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Cancel Interview
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
